// Notas ENRIQUECIDAS de ticket — helpers PUROS (sin DOM) para convertir entre
// el modo sencillo (texto plano + tokens `![alt](attachment:filename)`) y el
// modo rico (HTML del editor con `<img src="attachment:filename">`). Réplica
// del helper del CRM (crm/apps/web/src/shared/notes/richNote.ts).
//
// Modelo de datos (DECISIONS del CRM 2026-07-16.c): una nota rica persiste
// `bodyHtml` (el HTML) Y `body` (la derivación a texto plano CON los tokens de
// imagen). `body` sigue siendo la fuente de snippets, búsqueda e historial en
// el CRM, y el fallback de cualquier render que no sepa de HTML.
//
// Las imágenes NUNCA viajan inline en el HTML persistido: al guardar, cada
// `<img src="data:...">` (captura pegada en el editor) se EXTRAE a un adjunto
// normal y su src queda como `attachment:filename` — el mismo modelo privado
// (GCS + descarga autenticada) que los tokens del modo sencillo.
//
// String-based a propósito (no DOMParser): el HTML lo genera nuestro editor o
// ya pasó por este mismo circuito, y así los helpers son puros y testeables.

import {
  ATTACHMENT_TOKEN_SCHEME,
  inlineImageToken,
  makePastedImageFilename,
  parseNoteBody,
} from "./crmInlineImages";

/** Imagen extraída del HTML al guardar: adjunto listo para el POST. */
export interface ExtractedNoteImage {
  filename: string;
  contentType: string;
  contentBase64: string;
}

export const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** Decodifica las entidades que nosotros mismos generamos (+ &nbsp;). */
const decodeEntities = (s: string): string =>
  s
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/gi, "&");

/** Primer valor del atributo `name` de un tag serializado, o null. */
function tagAttr(tag: string, name: string): string | null {
  const m =
    new RegExp(`\\b${name}\\s*=\\s*"([^"]*)"`, "i").exec(tag) ??
    new RegExp(`\\b${name}\\s*=\\s*'([^']*)'`, "i").exec(tag);
  return m ? m[1] : null;
}

/** Reemplaza (o añade) el atributo src de un tag `<img …>` serializado. */
function withSrc(tag: string, src: string): string {
  if (/\bsrc\s*=\s*("[^"]*"|'[^']*')/i.test(tag)) {
    return tag.replace(/\bsrc\s*=\s*("[^"]*"|'[^']*')/i, () => `src="${escapeHtml(src)}"`);
  }
  return tag.replace(/^<img\b/i, `<img src="${escapeHtml(src)}"`);
}

/**
 * Texto plano (con tokens de imagen) → HTML editable. Cada línea es un `<p>`;
 * los tokens cuyo `resolveTokenSrc` devuelva un src (data: de un adjunto
 * pendiente) se convierten en `<img>` para verse en el editor; el resto se
 * queda como texto (fallback inocuo, igual que el render).
 */
export function plainTextToNoteHtml(
  text: string,
  resolveTokenSrc?: (filename: string) => string | null,
): string {
  if (text.trim().length === 0) return "";
  return text
    .split("\n")
    .map((line) => {
      if (line.trim().length === 0) return "<p></p>";
      const inner = parseNoteBody(line)
        .map((part) => {
          if (part.type === "text") return escapeHtml(part.value);
          const src = part.filename
            ? resolveTokenSrc?.(part.filename) ?? null
            : null;
          if (!src) {
            return escapeHtml(inlineImageToken(part.filename ?? "", part.value));
          }
          return `<img src="${escapeHtml(src)}" alt="${escapeHtml(part.value)}">`;
        })
        .join("");
      return `<p>${inner}</p>`;
    })
    .join("");
}

/**
 * HTML de nota → texto plano con tokens. Es la derivación que se persiste en
 * `body` junto al `bodyHtml` (y a la que se vuelve al desactivar el formato):
 * imágenes `attachment:` → token; bloques (`p`/`li`/`h*`/`div`/`br`…) → salto
 * de línea; el resto de tags se elimina y las entidades se decodifican.
 */
export function htmlToNotePlainText(html: string): string {
  let out = html;
  // Imágenes → token (attachment:) o alt (externas). Antes de tocar los bloques.
  out = out.replace(/<img\b[^>]*>/gi, (tag) => {
    const src = tagAttr(tag, "src") ?? "";
    const alt = tagAttr(tag, "alt") || "imagen";
    if (src.startsWith(ATTACHMENT_TOKEN_SCHEME)) {
      return inlineImageToken(src.slice(ATTACHMENT_TOKEN_SCHEME.length), alt);
    }
    return alt === "imagen" ? "" : `[${alt}]`;
  });
  out = out.replace(/<br\s*\/?>/gi, "\n");
  out = out.replace(/<\/(p|div|li|h[1-6]|tr|blockquote|ul|ol)>/gi, "\n");
  out = out.replace(/<[^>]+>/g, "");
  out = decodeEntities(out);
  // Compacta: cerrar ul/ol Y li dejaría huecos dobles tras cada lista.
  out = out.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n");
  return out.trim();
}

/**
 * ¿El HTML lleva formato que se PERDERÍA al volver a texto sencillo? (marcas,
 * listas, enlaces, títulos…). Las imágenes NO cuentan: sobreviven como
 * token/adjunto. Decide si el toggle a sencillo pide confirmación.
 */
export function htmlHasRichFormatting(html: string): boolean {
  return /<(strong|b|em|i|u|s|strike|ol|ul|a|h[1-6]|blockquote|code|pre|hr)\b/i.test(
    html,
  );
}

/**
 * Prepara el HTML del editor para PERSISTIR:
 *  - `<img src="data:image/...">` (captura pegada) → se extrae como adjunto
 *    nuevo y el src queda `attachment:filename`.
 *  - src conocido por `knownSrcFilename` (data: de un adjunto que YA está
 *    contabilizado como pendiente) → `attachment:filename`, SIN duplicarlo.
 *  - `attachment:`/`http(s):` se conservan tal cual.
 *  - blob:/desconocidos sin mapa → se eliminan (nadie podría resolverlos).
 * Devuelve también la derivación a texto plano (`body`).
 */
export function extractRichNoteContent(
  html: string,
  knownSrcFilename: (src: string) => string | null,
): { bodyHtml: string; body: string; images: ExtractedNoteImage[] } {
  const images: ExtractedNoteImage[] = [];
  const bodyHtml = html.replace(/<img\b[^>]*>/gi, (tag) => {
    const src = tagAttr(tag, "src");
    if (!src) return "";
    if (src.startsWith(ATTACHMENT_TOKEN_SCHEME) || /^https?:/i.test(src)) {
      return tag;
    }
    const known = knownSrcFilename(src);
    if (known) return withSrc(tag, `${ATTACHMENT_TOKEN_SCHEME}${known}`);
    const dataMatch = /^data:(image\/[a-z0-9.+-]+);base64,([a-z0-9+/=\s]+)$/i.exec(src);
    if (dataMatch) {
      const contentType = dataMatch[1].toLowerCase();
      const filename = makePastedImageFilename(contentType);
      images.push({
        filename,
        contentType,
        contentBase64: dataMatch[2].replace(/\s+/g, ""),
      });
      return withSrc(tag, `${ATTACHMENT_TOKEN_SCHEME}${filename}`);
    }
    return "";
  });
  return { bodyHtml, body: htmlToNotePlainText(bodyHtml), images };
}
